/* ============================================================
   Free Gift Progress Bar — cart logic
   Vanilla JS, no dependencies. Built for Horizon-style themes
   that dispatch a cart:updated CustomEvent on document.

   Responsibilities:
   - Recompute progress from the cart on every cart change
     (add / remove / quantity change) on every surface.
   - Add a gift only when its tier is unlocked; never twice.
   - Auto-remove gifts whose tier no longer qualifies (also
     catches gifts added directly via variant ID).
   - Lock gift line quantities to 1.
   - Multi-currency aware via Shopify.currency.rate.
   - Serialized mutation queue -> no race conditions from
     rapid clicks or overlapping cart requests.

   Each rendered block carries its own JSON config, so the same
   block can sit in the cart drawer and the cart page with
   different settings. Gift revalidation uses the first block's
   thresholds as the authority.
   ============================================================ */
(function () {
  'use strict';

  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?([/?#]|$)/;
  var SELF = 'free-gift-bar';

  /* ---------- Shopify money formatting (shop currency) ---------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var units = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      var sub = parts[1] ? decimal + parts[1] : '';
      return units + sub;
    }

    var value;
    switch ((format.match(placeholderRegex) || [])[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount':
      default:
        value = withDelimiters(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  }

  /* ---------- Merge tags: {{ amount_remaining }} etc. ---------- */
  function fillTemplate(template, data) {
    if (!template) return '';
    return template.replace(/\[(\w+)\]/g, function (match, key) {
      return data[key] != null ? data[key] : match;
    });
  }

  function FreeGiftBar() {
    this.queue = Promise.resolve();
    this.lastCart = null;
    this.refreshTimer = null;
    this.noticeTimer = null;
    this.bindEvents();
    this.scheduleRefresh(0);
  }

  FreeGiftBar.prototype = {
    /* ---------- mounts and their configs ---------- */
    mounts: function () {
      return Array.prototype.slice.call(document.querySelectorAll('[data-fgb]'));
    },

    configFor: function (mount) {
      var el = mount.querySelector('script[data-fgb-config]');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        console.error('[free-gift-bar] bad config JSON', e);
        return null;
      }
    },

    /* The block whose thresholds govern gift revalidation. */
    primaryConfig: function () {
      var mounts = this.mounts();
      for (var i = 0; i < mounts.length; i++) {
        var config = this.configFor(mounts[i]);
        if (config) return config;
      }
      return null;
    },

    /* ---------- currency helpers ---------- */
    presentmentRate: function (config) {
      var s = window.Shopify;
      if (s && s.currency && s.currency.active && s.currency.active !== config.shopCurrency) {
        var rate = parseFloat(s.currency.rate);
        if (isFinite(rate) && rate > 0) return rate;
      }
      return 1;
    },

    money: function (cents, config) {
      var s = window.Shopify;
      if (s && s.currency && s.currency.active && s.currency.active !== config.shopCurrency) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency',
            currency: s.currency.active
          }).format(cents / 100);
        } catch (e) { /* fall through to shop money format */ }
      }
      return formatMoney(cents, config.moneyFormat || ('$' + '{{amount}}'));
    },

    /* ---------- cart I/O ---------- */
    getCart: function (config) {
      return fetch(config.routes.cart, { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json(); });
    },

    postJSON: function (url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
    },

    /* ---------- state ---------- */
    computeState: function (cart, config) {
      var rate = this.presentmentRate(config);
      var giftLines = [];
      var subtotal = 0;

      (cart.items || []).forEach(function (item) {
        if (item.properties && item.properties._free_gift) {
          giftLines.push(item);
        } else {
          subtotal += item.final_line_price;
        }
      });

      var maxThreshold = 0;
      var tiers = (config.tiers || []).map(function (tier) {
        var threshold = Math.round(tier.threshold * rate);
        if (threshold > maxThreshold) maxThreshold = threshold;
        var line = null;
        for (var i = 0; i < giftLines.length; i++) {
          if (giftLines[i].properties._free_gift === tier.id) { line = giftLines[i]; break; }
        }
        return {
          id: tier.id,
          config: tier,
          threshold: threshold,
          unlocked: subtotal >= threshold,
          line: line,
          available: tier.available
        };
      });

      return {
        cart: cart,
        config: config,
        subtotal: subtotal,
        giftLines: giftLines,
        tiers: tiers,
        maxThreshold: maxThreshold
      };
    },

    /* ---------- guard: remove gifts that no longer qualify ----------
       Client-side only. Returns the (possibly updated) cart. */
    revalidate: function (state) {
      var self = this;
      var config = state.config;
      var updates = {};
      var removed = [];
      var seenTiers = {};

      state.giftLines.forEach(function (line) {
        var tierId = line.properties._free_gift;
        var tier = null;
        for (var i = 0; i < state.tiers.length; i++) {
          if (state.tiers[i].id === tierId) { tier = state.tiers[i]; break; }
        }

        var qualifies =
          tier &&
          line.variant_id === tier.config.variantId &&
          state.subtotal >= tier.threshold &&
          !seenTiers[tierId];

        if (!qualifies) {
          updates[line.key] = 0;
          removed.push({ title: line.product_title, threshold: tier ? tier.threshold : null });
        } else {
          seenTiers[tierId] = true;
          if (line.quantity !== 1) updates[line.key] = 1;
        }
      });

      if (Object.keys(updates).length === 0) {
        return Promise.resolve(state.cart);
      }

      return this.postJSON(config.routes.cartUpdate, this.withSections({ updates: updates }))
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          removed.forEach(function (item) {
            self.notify(fillTemplate(
              config.text.removed || '{{ gift_title }} was removed from your cart.',
              {
                gift_title: item.title,
                threshold: item.threshold != null ? self.money(item.threshold, config) : ''
              }
            ));
          });
          self.updateCartCounts(cart);
          self.applyThemeUpdate(cart, cart && cart.sections);
          return cart;
        });
    },

    /* ---------- rendering ---------- */
    renderCart: function (cart) {
      var self = this;
      this.mounts().forEach(function (mount) {
        var config = self.configFor(mount);
        if (!config) return;
        self.renderMount(mount, self.computeState(cart, config));
      });
      var primary = this.primaryConfig();
      if (primary) this.lockGiftLineQuantities(this.computeState(cart, primary));
    },

    renderMount: function (mount, state) {
      var self = this;
      var config = state.config;
      var pct = state.maxThreshold > 0
        ? Math.min(100, (state.subtotal / state.maxThreshold) * 100)
        : 0;

      var headline = config.text.complete || '';
      for (var i = 0; i < state.tiers.length; i++) {
        if (!state.tiers[i].unlocked) {
          headline = fillTemplate(state.tiers[i].config.lockedMsg, self.tierData(state.tiers[i], state));
          break;
        }
      }

      var bar = mount.querySelector('[data-fgb-bar]');
      var fill = mount.querySelector('[data-fgb-fill]');
      var headlineEl = mount.querySelector('[data-fgb-headline]');

      if (fill) fill.style.width = pct.toFixed(1) + '%';
      if (bar) {
        bar.setAttribute('aria-valuenow', String(Math.round(pct)));
        bar.setAttribute(
          'aria-valuetext',
          self.money(state.subtotal, config) + ' of ' + self.money(state.maxThreshold, config)
        );
      }
      if (headlineEl && config.showRemaining) headlineEl.textContent = headline;

      state.tiers.forEach(function (tier) {
        var marker = mount.querySelector('[data-fgb-marker="' + tier.id + '"]');
        if (marker) marker.classList.toggle('is-unlocked', tier.unlocked);

        var row = mount.querySelector('[data-fgb-tier="' + tier.id + '"]');
        if (!row) return;

        row.classList.toggle('is-unlocked', tier.unlocked);
        row.classList.toggle('is-added', !!tier.line);

        var msgEl = row.querySelector('[data-fgb-msg]');
        if (msgEl) {
          msgEl.textContent = fillTemplate(
            tier.unlocked ? tier.config.unlockedMsg : tier.config.lockedMsg,
            self.tierData(tier, state)
          );
        }

        var addForm = row.querySelector('[data-fgb-add-form]');
        if (addForm) addForm.hidden = !(tier.unlocked && tier.available && !tier.line);

        var removeBtn = row.querySelector('[data-fgb-remove]');
        if (removeBtn) {
          removeBtn.hidden = !tier.line;
          removeBtn.dataset.key = tier.line ? tier.line.key : '';
        }

        var oosEl = row.querySelector('[data-fgb-oos]');
        if (oosEl) oosEl.hidden = !(tier.unlocked && !tier.available && !tier.line);
      });
    },

    tierData: function (tier, state) {
      var config = state.config;
      return {
        amount_remaining: this.money(Math.max(0, tier.threshold - state.subtotal), config),
        threshold: this.money(tier.threshold, config),
        gift_title: tier.config.title
      };
    },

    /* Hide the quantity widget on gift line items. Best effort on
       heavily customized carts — revalidation resets any quantity
       back to 1 regardless. */
    lockGiftLineQuantities: function (state) {
      document.querySelectorAll('.fgb-qty-locked').forEach(function (el) {
        el.classList.remove('fgb-qty-locked');
      });
      document.querySelectorAll('.fgb-qty-static').forEach(function (el) { el.remove(); });
      if (!state.giftLines.length) return;

      var giftVariantIds = {};
      state.giftLines.forEach(function (line) { giftVariantIds[line.variant_id] = true; });

      var selector = 'input[data-quantity-variant-id], input[name="updates[]"], input[data-variant-id]';
      document.querySelectorAll(selector).forEach(function (input) {
        var vid = input.getAttribute('data-quantity-variant-id') || input.getAttribute('data-variant-id');
        if (!vid || !giftVariantIds[vid]) return;
        input.readOnly = true;
        var widget = input.closest('quantity-input, quantity-selector-component, .quantity') || input.parentElement;
        if (widget && !widget.classList.contains('fgb-qty-locked')) {
          widget.classList.add('fgb-qty-locked');
          var badge = document.createElement('span');
          badge.className = 'fgb-qty-static';
          badge.textContent = '1';
          widget.insertAdjacentElement('afterend', badge);
        }
      });
    },

    notify: function (message) {
      var self = this;
      this.mounts().forEach(function (mount) {
        var el = mount.querySelector('[data-fgb-notice]');
        if (el) el.textContent = message;
      });
      clearTimeout(this.noticeTimer);
      this.noticeTimer = setTimeout(function () {
        self.mounts().forEach(function (mount) {
          var el = mount.querySelector('[data-fgb-notice]');
          if (el) el.textContent = '';
        });
      }, 6000);
    },

    /* ---------- theme cart re-render (Section Rendering API) ----------
       This theme's <cart-items-component> / <cart-drawer-items> only
       repaint when they are handed rendered section HTML: either inside
       a cart:updated event (detail.data.sections) or by fetching the
       section themselves. Without that a gift line only shows up after
       a full page reload. */
    themeCartComponents: function () {
      return Array.prototype.slice.call(
        document.querySelectorAll('cart-items-component, cart-drawer-items')
      );
    },

    themeSectionIds: function () {
      var ids = [];
      this.themeCartComponents().forEach(function (el) {
        var id = el.dataset && el.dataset.sectionId;
        if (id && ids.indexOf(id) === -1) ids.push(id);
      });
      if (!ids.length) {
        var fallback = document.querySelector('[data-section-id*="cart"]');
        var fallbackId = fallback && fallback.dataset && fallback.dataset.sectionId;
        if (fallbackId) ids.push(fallbackId);
      }
      return ids;
    },

    designMode: function () {
      return !!(window.Shopify && window.Shopify.designMode);
    },

    /* Ask the Cart AJAX API for the rendered cart sections alongside the
       cart. Skipped in the theme editor, where the theme re-fetches
       sections itself so the editor keeps its own markup. */
    withSections: function (body) {
      if (this.designMode()) return body;
      var ids = this.themeSectionIds();
      if (ids.length) {
        body.sections = ids.join(',');
        body.sections_url = window.location.pathname;
      }
      return body;
    },

    /* Fallback: let every cart component re-fetch its own section. */
    renderThemeSections: function () {
      var jobs = [];
      this.themeCartComponents().forEach(function (el) {
        var id = el.dataset && el.dataset.sectionId;
        if (!id || typeof el.renderSection !== 'function') return;
        try {
          jobs.push(Promise.resolve(el.renderSection(id)).catch(function () {}));
        } catch (e) { /* component not upgraded yet */ }
      });
      return Promise.all(jobs);
    },

    /* Repaint the cart surfaces, then announce what changed. */
    applyThemeUpdate: function (cart, sections) {
      var self = this;
      if (!this.designMode() && sections && Object.keys(sections).length) {
        this.announceThemeUpdate(cart, sections);
        return Promise.resolve();
      }
      return this.renderThemeSections().then(function () {
        self.announceThemeUpdate(cart, null);
      });
    },

    /* Header bubble and "N items" labels, same contract as the theme. */
    updateCartCounts: function (cart) {
      if (!cart || cart.item_count == null) return;
      var count = cart.item_count;
      var strings = window.cartStrings || {};
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
      });
      if (count >= 100 && strings.item_99) {
        document.querySelectorAll('.cart-count-bubble [data-cart-count]').forEach(function (el) {
          el.textContent = strings.item_99;
        });
      }
      document.querySelectorAll('[data-cart-text]').forEach(function (el) {
        var template = count === 1 ? strings.item : strings.items;
        if (template) el.textContent = template.replace('[count]', count);
      });
    },

    /* ---------- actions ---------- */
    addGift: function (tierId, button, mount) {
      var self = this;
      return this.enqueue(function () {
        var config = (mount && self.configFor(mount)) || self.primaryConfig();
        if (!config) return Promise.resolve();

        return self.getCart(config).then(function (cart) {
          var state = self.computeState(cart, config);
          var tier = null;
          for (var i = 0; i < state.tiers.length; i++) {
            if (state.tiers[i].id === tierId) { tier = state.tiers[i]; break; }
          }

          if (!tier || tier.line || !tier.unlocked) {
            self.renderCart(cart);
            return;
          }
          if (!tier.available) {
            self.notify(config.text.oos || 'This gift is currently unavailable.');
            self.renderCart(cart);
            return;
          }

          if (button) button.disabled = true;

          return self.postJSON(config.routes.cartAdd, self.withSections({
            items: [{ id: tier.config.variantId, quantity: 1, properties: { _free_gift: tier.id } }]
          }))
            .then(function (res) {
              if (!res.ok) {
                return res.json().catch(function () { return {}; }).then(function (err) {
                  self.notify(err.description || err.message || config.text.oos || 'Could not add the gift.');
                });
              }
              return res.json().catch(function () { return {}; }).then(function (payload) {
                return self.getCart(config).then(function (freshCart) {
                  self.lastCart = freshCart;
                  self.updateCartCounts(freshCart);
                  return self.applyThemeUpdate(freshCart, payload && payload.sections)
                    .then(function () { self.renderCart(freshCart); });
                });
              });
            })
            .finally(function () { if (button) button.disabled = false; });
        });
      });
    },

    removeGift: function (lineKey, button, mount) {
      var self = this;
      if (!lineKey) return Promise.resolve();
      return this.enqueue(function () {
        var config = (mount && self.configFor(mount)) || self.primaryConfig();
        if (!config) return Promise.resolve();
        if (button) button.disabled = true;

        return self.postJSON(config.routes.cartChange, self.withSections({ id: lineKey, quantity: 0 }))
          .then(function (res) { return res.json(); })
          .then(function (cart) {
          self.lastCart = cart;
          self.updateCartCounts(cart);
          return self.applyThemeUpdate(cart, cart && cart.sections)
            .then(function () { self.renderCart(cart); });
        })
          .finally(function () { if (button) button.disabled = false; });
      });
    },

    /* ---------- refresh cycle ---------- */
    refresh: function () {
      var self = this;
      return this.enqueue(function () {
        var config = self.primaryConfig();
        if (!config) return Promise.resolve();

        return self.getCart(config)
          .then(function (cart) {
            return self.revalidate(self.computeState(cart, config)).then(function (finalCart) {
              self.lastCart = finalCart;
              self.renderCart(finalCart);
            });
          })
          .catch(function (e) { console.error('[free-gift-bar]', e); });
      });
    },

    scheduleRefresh: function (delay) {
      var self = this;
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(function () {
        self.refresh().then(function () {
          /* Second pass: the theme swaps cart / drawer HTML slightly
             after its own fetch resolves, which wipes the first pass. */
          setTimeout(function () {
            if (self.lastCart) self.renderCart(self.lastCart);
          }, 500);
        });
      }, delay == null ? 150 : delay);
    },

    /* Serialize every cart mutation and refresh. */
    enqueue: function (fn) {
      this.queue = this.queue.then(fn, fn).catch(function (e) {
        console.error('[free-gift-bar]', e);
      });
      return this.queue;
    },

    /* Tell the theme our fetch changed the cart so it re-renders. */
    announceThemeUpdate: function (cart, sections) {
      var payload = cart;
      if (sections) {
        payload = {};
        Object.keys(cart || {}).forEach(function (key) { payload[key] = cart[key]; });
        payload.sections = sections;
      }
      try {
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { source: SELF, cart: cart, data: payload, sections: sections || null }
        }));
      } catch (e) { /* non-fatal */ }
      try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: SELF, cartData: cart });
        }
      } catch (e) { /* non-fatal */ }
    },

    /* ---------- event wiring ---------- */
    bindEvents: function () {
      var self = this;

      /* Delegated so the handlers survive drawer re-renders. */
      document.addEventListener('submit', function (event) {
        var form = event.target && event.target.closest && event.target.closest('[data-fgb-add-form]');
        if (!form) return;
        event.preventDefault();
        self.addGift(form.dataset.tier, form.querySelector('button'), form.closest('[data-fgb]'));
      }, true);

      document.addEventListener('click', function (event) {
        var btn = event.target && event.target.closest && event.target.closest('[data-fgb-remove]');
        if (!btn) return;
        event.preventDefault();
        self.removeGift(btn.dataset.key, btn, btn.closest('[data-fgb]'));
      });

      /* This theme's cart event. Ignore the ones we fire ourselves. */
      document.addEventListener('cart:updated', function (event) {
        if (event && event.detail && event.detail.source === SELF) {
          self.scheduleRefresh(400);
        } else {
          self.scheduleRefresh();
        }
      });

      /* Other common theme/app cart events. */
      ['cart:refresh', 'cart:change', 'cart-drawer:open'].forEach(function (name) {
        document.addEventListener(name, function () { self.scheduleRefresh(); });
      });

      /* Dawn-style pub/sub, if this theme ever gains it. */
      try {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
            if (!event || event.source !== SELF) self.scheduleRefresh();
          });
        }
      } catch (e) { /* ignore */ }

      /* Safety net: watch every fetch to the Cart AJAX API, whichever
         component makes it. */
      if (window.fetch && !window.fetch.__fgbWrapped) {
        var originalFetch = window.fetch;
        var wrapped = function () {
          var promise = originalFetch.apply(this, arguments);
          try {
            var input = arguments[0];
            var url = typeof input === 'string' ? input : (input && input.url) || '';
            if (CART_MUTATION_RE.test(url)) {
              promise.finally(function () { self.scheduleRefresh(); });
            }
          } catch (e) { /* never break the page's own fetch */ }
          return promise;
        };
        wrapped.__fgbWrapped = true;
        window.fetch = wrapped;
      }

      /* Theme editor re-renders a section or block. */
      document.addEventListener('shopify:section:load', function () { self.scheduleRefresh(); });
      document.addEventListener('shopify:block:select', function () { self.scheduleRefresh(); });
    }
  };

  function init() {
    if (window.__freeGiftBar) return;
    if (document.querySelector('script[data-fgb-config]')) {
      window.__freeGiftBar = new FreeGiftBar();
    } else {
      var observer = new MutationObserver(function () {
        if (document.querySelector('script[data-fgb-config]')) {
          observer.disconnect();
          if (!window.__freeGiftBar) window.__freeGiftBar = new FreeGiftBar();
        }
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
